import React, { useCallback, useEffect, useState } from "react";

import getUserPlan from "@/features/user/utils/getUserPlan";
import { cn } from "@/utils";

import {
  initCappedState,
  isIndicatorHidden,
  markCappedAcknowledged,
  markIndicatorHidden,
  markPulseShown,
  shouldShowPulse,
} from "./cappedConversationStorage";
import ContextCapPopover from "./ContextCapPopover";
import type { ContextCapIndicatorProps } from "./types";
import { useEffectiveCappedMessageId } from "./useEffectiveCappedMessageId";

const PULSE_DURATION_MS = 10000; // 10 seconds

const ContextCapIndicator = ({ conversationId, cappedMessageId, className, isCutoffMessageLoaded, onJumpToCutoff }: ContextCapIndicatorProps) => {
  const { hasUserSubscribedPlan } = getUserPlan();
  const [stateIsPopoverOpen, setStateIsPopoverOpen] = useState(false);
  const [stateIsHidden, setStateIsHidden] = useState(() => (conversationId ? isIndicatorHidden({ conversationId }) : false));
  const [stateHideOnClose, setStateHideOnClose] = useState(false);

  // Get effective cappedMessageId - use stored value if prop is undefined (API hasn't responded yet)
  const effectiveCappedMessageId = useEffectiveCappedMessageId({
    conversationId: conversationId ?? null,
    cappedMessageId,
  });

  // Compute initial pulse state from storage
  const initialShouldPulse =
    conversationId && effectiveCappedMessageId ? shouldShowPulse({ conversationId, lastCappedMessageId: effectiveCappedMessageId }) : true;

  const [stateShouldPulse, setStateShouldPulse] = useState(initialShouldPulse);
  const [stateHasBeenClicked, setStateHasBeenClicked] = useState(!initialShouldPulse);

  // Determine if effectively capped (from prop or stored value)
  const isEffectivelyCapped = Boolean(effectiveCappedMessageId);

  // Initialize storage entry immediately when capped state is detected (from API prop)
  useEffect(() => {
    if (conversationId && cappedMessageId) {
      initCappedState({ conversationId, lastCappedMessageId: cappedMessageId });
    }
  }, [conversationId, cappedMessageId]);

  // Update state when conversation/cappedMessageId changes (e.g., navigating between chats)
  useEffect(() => {
    if (isEffectivelyCapped && conversationId && effectiveCappedMessageId) {
      const shouldPulseNow = shouldShowPulse({ conversationId, lastCappedMessageId: effectiveCappedMessageId });
      setStateShouldPulse(shouldPulseNow);
      setStateHasBeenClicked(!shouldPulseNow);
      // Re-derive hidden state from storage when conversation changes
      setStateIsHidden(isIndicatorHidden({ conversationId }));
    }
    // Reset popover and checkbox state to prevent cross-conversation leakage
    setStateIsPopoverOpen(false);
    setStateHideOnClose(false);
  }, [isEffectivelyCapped, conversationId, effectiveCappedMessageId]);

  // Stop pulsing after 10 seconds and persist to storage
  useEffect(() => {
    if (!isEffectivelyCapped || !stateShouldPulse || !conversationId || !effectiveCappedMessageId) return;

    const timer = setTimeout(() => {
      setStateShouldPulse(false);
      markPulseShown({ conversationId, lastCappedMessageId: effectiveCappedMessageId });
    }, PULSE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [isEffectivelyCapped, stateShouldPulse, conversationId, effectiveCappedMessageId]);

  const handleClick = useCallback(() => {
    setStateHasBeenClicked(true);
    setStateIsPopoverOpen((prev: boolean) => !prev);
    if (conversationId && effectiveCappedMessageId) {
      markCappedAcknowledged({ conversationId, lastCappedMessageId: effectiveCappedMessageId });
    }
  }, [conversationId, effectiveCappedMessageId]);

  const handleClose = useCallback(() => {
    if (stateHideOnClose && conversationId) {
      markIndicatorHidden({ conversationId });
      setStateIsHidden(true);
    }
    setStateIsPopoverOpen(false);
    setStateHideOnClose(false);
  }, [stateHideOnClose, conversationId]);

  const handleStopPropagation = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleToggleHideOnClose = useCallback(() => {
    setStateHideOnClose((prev) => !prev);
  }, []);

  // Don't render if not capped or if user chose to hide
  if (!isEffectivelyCapped || stateIsHidden) return null;

  // Subtle gray indicator with gentle pulse animation
  return (
    <div className="relative" data-testid="ContextCapIndicator">
      <button
        onClick={handleClick}
        className={cn(
          "w-3 h-3 rounded-full cursor-pointer",
          "bg-gray-9 border border-gray-11",
          !stateHasBeenClicked && stateShouldPulse && "animate-pulse",
          className,
        )}
        aria-label="Context limit reached"
        data-testid="ContextCapIndicator-Button"
      />
      <ContextCapPopover
        isOpen={stateIsPopoverOpen}
        hasUserSubscribedPlan={hasUserSubscribedPlan}
        hideOnClose={stateHideOnClose}
        onClose={handleClose}
        onStopPropagation={handleStopPropagation}
        onToggleHideOnClose={handleToggleHideOnClose}
        isCutoffMessageLoaded={isCutoffMessageLoaded}
        onJumpToCutoff={onJumpToCutoff}
      />
    </div>
  );
};

export default ContextCapIndicator;
